import React from "react";
import { MIcon } from "src/theme";
import { PATH_APP } from "src/routes/paths";
import Roles from "src/utils/Roles";
import ROLES_ENUM from "src/utils/enums";
import { PROMOTIONS_ADDON_NAME } from "src/utils/loyalty/loyaltyRules";
import Label from "src/components/Label";
import { INVENTARIO_ADDON_NAME, textoBadge } from "src/utils/gestion/inventarioHub";

// ----------------------------------------------------------------------

const path = (name) => `/static/icons/navbar/${name}.svg`;

const ICONS = {
  authenticator: <MIcon src={path("ic_authenticator")} />,
  blog: <MIcon src={path("ic_blog")} />,
  calendar: <MIcon src={path("ic_calendar")} />,
  cart: <MIcon src={path("ic_cart")} />,
  charts: <MIcon src={path("ic_charts")} />,
  chat: <MIcon src={path("ic_chat")} />,
  components: <MIcon src={path("ic_components")} />,
  dashboard: <MIcon src={path("ic_dashboard")} />,
  editor: <MIcon src={path("ic_editor")} />,
  elements: <MIcon src={path("ic_elements")} />,
  error: <MIcon src={path("ic_error")} />,
  mail: <MIcon src={path("ic_mail")} />,
  map: <MIcon src={path("ic_map")} />,
  page: <MIcon src={path("ic_page")} />,
  user: <MIcon src={path("ic_user")} />,
  upload: <MIcon src={path("ic_upload")} />,
  copy: <MIcon src={path("ic_copy")} />,
  carousel: <MIcon src={path("ic_carousel")} />,
  language: <MIcon src={path("ic_language")} />,
  table: <MIcon src={path("ic_table")} />,
  settings: <MIcon src={path("ic_settings")} />,
  caja: <MIcon src={path("ic_caja")} />,
  tpv: <MIcon src={path("ic_tpv")} />,
  franquicias: <MIcon src={path("ic_franquicias")} />,
  help: <MIcon src={path("ic_question_circle")} />,
  logs: <MIcon src={path("ic_logs")} />,
  employees: <MIcon src={path("ic_employees")} />,
  categorie: <MIcon src={path("ic_list_categorie")} />,
  invoice: <MIcon src={path("ic_invoice")} />,
  discounts: <MIcon src={path("ic_discounts")} />,
  tags: <MIcon src={path("ic_tags")} />,
  displays: <MIcon src={path("ic_displays")} />,
  monitor: <MIcon src={path("ic_monitor")} />,
  email: <MIcon src={path("ic_email")} />,
};

const navConfig = (
  user,
  conditionTpv,
  employeeRole = [],
  displays = [],
  activeIntegrationCodes = [],
  activeAddOnNames = [],
  { isEmployeeWebSession = false, socialAllowed = false, stockAlertas = 0 } = {},
) => {
  // 2026-10 · Contador de alertas de stock junto a «Inventario y costes».
  const stockBadge = textoBadge(stockAlertas)
    ? <Label color="error" sx={{ mr: 1 }} data-testid="nav-stock-alertas">{textoBadge(stockAlertas)}</Label>
    : undefined;
  const actions = employeeRole;

  // GENERAL
  // ----------------------------------------------------------------------
  return {
    subheader: "tuCarta",
    items: [
      {
        // "Usuario" — sección "Mi cuenta" del dueño (perfil restaurante,
        // notificaciones, facturación SaaS). Gate EXCLUSIVO por las 3
        // acciones del grupo Account. No mezclar con AUDIT_VIEW/AEAT_ACCESS
        // (esos viven bajo "Configuración" con sub-items propios). Si se
        // metían aquí, un rol con solo Monitorización veía la pestaña y
        // al entrar la ruta `/user` respondía 403 porque su gate exige
        // PROFILE_MANAGE. Reportado 2026-08-27.
        title: "Usuario",
        icon: ICONS.user,
        href: PATH_APP.user.root,
        roles: [
          Roles.USER_NORMAL,
          Roles.USER_FRANQUICIADO,
          Roles.USER_FRANQUICIA,
        ],
        isAdmin: true,
        condition:
          actions.includes("PROFILE_MANAGE") ||
          actions.includes("ACCOUNT_NOTIFICATIONS_MANAGE") ||
          actions.includes("BILLING_MANAGE"),
      },
      // Portal del empleado — solo visible cuando la sesión es la del
      // trabajador (login web con email+password o QR). El dueño no
      // necesita esta vista, tiene el panel completo de empleados.
      // Insertamos condicionalmente porque el filtrado por `condition`
      // solo aplica a items `isAdmin=true` cuando `requestUserIdentity`
      // está activo — meter el item con `condition=false` lo dejaría
      // visible al dueño.
      ...(isEmployeeWebSession ? [{
        title: "Mi portal",
        icon: ICONS.employees,
        href: PATH_APP.user.myPortal,
        roles: [Roles.USER_NORMAL],
        isAdmin: false,
      }] : []),
      ...navConfigNormalCustomer(user, conditionTpv, actions, displays, activeIntegrationCodes, activeAddOnNames, stockBadge),
      ...navConfigFranquicia(user, conditionTpv, actions),
      ...navConfigFranquiciado(user, conditionTpv, actions),
      ...navConfigAdmin(user, conditionTpv, actions, { socialAllowed }),
      {
        // "Pedir ayuda" siempre visible — no requiere permiso especial.
        // 2026-08-25 · Antes gateado por `HELP_MANAGER` que ni siquiera
        // está en el catálogo backend, así que se ocultaba a todo
        // empleado. Es un item universal: cualquiera del equipo puede
        // pedir ayuda al soporte.
        title: "Pedir ayuda",
        icon: ICONS.help,
        href: PATH_APP.user.contact,
        condition: true,
        roles: [
          Roles.USER_FRANQUICIA,
          Roles.USER_FRANQUICIADO,
          Roles.USER_NORMAL,
        ],
        isAdmin: true,
      },
    ],
    bottomItems: [],
  };
};

const navConfigNormalCustomer = (
  user,
  conditionTpv,
  actions = [],
  displays = [],
  activeIntegrationCodes = [],
  activeAddOnNames = [],
  stockBadge = undefined,
) => {
  const displayItems =
    displays.map((display) => ({
      title: display.name,
      icon: ICONS.displays,
      href: PATH_APP.root + `/tpv/displays/${display.id}`,
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition: actions.includes(ROLES_ENUM.INVENTARY_DISPLAYS_MANAGER),
    })) || [];

  return [
    {
      title: "TPV",
      icon: ICONS.tpv,
      href: conditionTpv ? PATH_APP.tpv.cartaCamarero : PATH_APP.tuCarta.tpv,
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition: actions.includes(ROLES_ENUM.TPV_MANAGER),
    },
    {
      title: "Caja",
      icon: ICONS.caja,
      href: PATH_APP.tuCarta.caja,
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition:
        actions.includes(ROLES_ENUM.ACCESS_CAJA) || user?.cajaAvailable,
    },
    {
      title: "Empleados",
      icon: ICONS.employees,
      href: PATH_APP.tuCarta.employees,
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition: actions.includes(ROLES_ENUM.RESTAURANT_EMPLOYEE_MANAGER),
    },
    // Festivos: movido dentro del tab "Festivos" de la vista Empleados.
    // La ruta legacy /tucarta/employees/festivos sigue funcionando por
    // si el user tenía la URL guardada.
    {
      // F64 · KDS premium — requiere add-on "Pantalla de cocina" o grant.
      // Sin él, el `isAddOnCta` hace que `NavItem` pinte un badge PRO
      // dorado a la derecha del título en vez del sufijo "Contrátalo".
      title: "Pantallas de cocina",
      icon: ICONS.displays,
      href: activeAddOnNames.includes("Pantalla de cocina")
        ? PATH_APP.tuCarta.displays
        : "#addon:Pantalla de cocina",
      isAddOnCta: !activeAddOnNames.includes("Pantalla de cocina"),
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition:
        actions.includes(ROLES_ENUM.INVENTARY_DISPLAYS_MANAGER) &&
        (activeAddOnNames.includes("Pantalla de cocina") || displays.length > 0),
      items: activeAddOnNames.includes("Pantalla de cocina") ? [
        {
          title: "Pantallas",
          href: PATH_APP.tuCarta.displays,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.INVENTARY_DISPLAYS_MANAGER),
        },
        ...displayItems,
      ] : undefined,
    },
    {
      title: "Mi restaurante",
      icon: ICONS.franquicias,
      href: PATH_APP.tuCarta.mesas,
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition:
        actions.includes(ROLES_ENUM.RESTAURANT_MANAGER) ||
        user?.tablesAvailable,
      items: [
        {
          title: "Mesas",
          href: PATH_APP.tuCarta.mesas,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition:
            actions.includes(ROLES_ENUM.RESTAURANT_TABLE_MANAGER) ||
            user?.tablesAvailable,
        },
        {
          // F64 · Reservas premium — requiere add-on "Reservas online"
          // o grant admin. Sin él, `isAddOnCta=true` pinta badge PRO
          // dorado y el click abre modal Stripe directo.
          title: "Reservas",
          icon: ICONS.calendar,
          href: activeAddOnNames.includes("Reservas online")
            ? PATH_APP.tuCarta.reservas
            : "#addon:Reservas online",
          isAddOnCta: !activeAddOnNames.includes("Reservas online"),
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition:
            actions.includes(ROLES_ENUM.BOOKING_VIEW) ||
            actions.includes(ROLES_ENUM.RESTAURANT_BOOKING_MANAGER),
        },
        {
          // Add-on "Pedidos" — pedidos desde carta QR (base de futuros
          // canales como delivery). Sin add-on/grant: badge PRO y el
          // click abre el modal de contratación.
          title: "Pedidos",
          icon: ICONS.cart,
          href: activeAddOnNames.includes("Pedidos")
            ? PATH_APP.tuCarta.pedidos
            : "#addon:Pedidos",
          isAddOnCta: !activeAddOnNames.includes("Pedidos"),
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.ORDERS_MANAGE),
        },
        {
          // Add-on "Promociones" — tarjetas de sellos / fidelización
          // (p. ej. cada 10 cafés, 1 gratis). Sin add-on: badge PRO.
          // En el menú: «Fidelización (sellos)» (el add-on y la ruta
          // conservan el nombre «Promociones»).
          title: "Fidelización (sellos)",
          icon: ICONS.discounts,
          href: activeAddOnNames.includes(PROMOTIONS_ADDON_NAME)
            ? PATH_APP.tuCarta.promociones
            : `#addon:${PROMOTIONS_ADDON_NAME}`,
          isAddOnCta: !activeAddOnNames.includes(PROMOTIONS_ADDON_NAME),
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.LOYALTY_MANAGE),
        },
        {
          title: "Descuentos",
          icon: ICONS.discounts,
          href: PATH_APP.tuCarta.discount,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.RESTAURANT_DISCOUNT_MANAGER),
        },
        // "Sesiones" (enlaces y QR de mesas) → tab "Códigos QR" de Mesas.
        {
          title: "Métodos de pago",
          href: PATH_APP.tuCarta.paymentMethods,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(
            ROLES_ENUM.RESTAURANT_PAYMENT_METHODS_MANAGER,
          ),
        },
      ],
    },
    {
      title: "Inventario",
      icon: ICONS.table,
      href: PATH_APP.tuCarta.productos,
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition:
        actions.includes(ROLES_ENUM.INVENTARY_MANAGER) ||
        user?.productsAvailable ||
        user?.categoriesAvailable,
      items: [
        {
          // F74 · Escandallo/stock premium — requiere add-on. Sin él,
          // `isAddOnCta=true` pinta badge PRO dorado y el click abre
          // modal Stripe directo.
          // 2026-08-24 · Además gate por rol INVENTARY_GESTION_MANAGER.
          // Sin permiso el item desaparece del nav (aunque tenga
          // addon contratado).
          // 2026-10 · «Inventario y costes» sustituye a Dashboard y
          // Operaciones (sus rutas redirigen al hub) y lleva el contador
          // de alertas de stock en tiempo real.
          title: "Inventario y costes",
          href: activeAddOnNames.includes(INVENTARIO_ADDON_NAME)
            ? PATH_APP.tuCarta.inventario
            : `#addon:${INVENTARIO_ADDON_NAME}`,
          isAddOnCta: !activeAddOnNames.includes(INVENTARIO_ADDON_NAME),
          info: activeAddOnNames.includes(INVENTARIO_ADDON_NAME) ? stockBadge : undefined,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.INVENTARY_GESTION_MANAGER),
        },
        {
          // "Productos" es VER la carta — la gestión (crear/editar/
          // borrar) va gated dentro de la vista con PRODUCT_CREATE /
          // PRODUCT_MODIFY / PRODUCT_DELETE. Sin este cambio, un rol
          // con CATEGORY_MANAGE veía "Categorías" pero no "Productos",
          // resultando en un nav coherente semánticamente pero
          // confuso para el user (para editar categorías necesitas
          // saber qué productos hay).
          title: "Productos",
          href: PATH_APP.tuCarta.productos,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition:
            actions.includes("PRODUCT_VIEW") ||
            actions.includes(ROLES_ENUM.INVENTARY_PRODUCT_MANAGER) ||
            user?.productsAvailable,
        },
        {
          title: "Categorías",
          href: PATH_APP.tuCarta.categories,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition:
            actions.includes(ROLES_ENUM.INVENTARY_CATEGORIES_MANAGER) ||
            user?.categoriesAvailable,
        },
        {
          title: "Etiquetas",
          href: PATH_APP.tuCarta.tags,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition:
            actions.includes(ROLES_ENUM.INVENTARY_TAGS_MANAGER) ||
            user?.categoriesAvailable,
        },
        {
          // Precios y promociones: pestañas Happy hour · Suplementos por
          // zona · Informe (cada una según su ajuste). Permiso
          // PRICE_RULES_MANAGE; con ambos ajustes apagados la página
          // explica cómo activarlos.
          title: "Precios y promociones",
          href: PATH_APP.tuCarta.precios,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.PRICE_RULES_MANAGE),
        },
      ],
    },
    {
      title: "Facturación",
      icon: ICONS.invoice,
      href: PATH_APP.tuCarta.dashboard,
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition: actions.includes(ROLES_ENUM.BILLING_MANAGER),
      items: [
        {
          title: "Clientes",
          href: PATH_APP.tuCarta.factura,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.BILLING_CUSTOMERS_MANAGER),
        },
        {
          title: "Estadísticas",
          href: PATH_APP.tuCarta.estadisticas,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.BILLING_ANALYTICS_MANAGER),
        },
        {
          title: "Facturas",
          href: PATH_APP.tuCarta.invoices,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.BILLING_INVOICES_MANAGER),
        },
        // {
        //   title: "Actividad",
        //   href: PATH_APP.tuCarta.logs,
        //   roles: [Roles.USER_NORMAL],
        //   isAdmin: true,
        //   condition: actions.includes(ROLES_ENUM.BILLING_ACTIVITY_MANAGER),
        // },
      ],
    },
    {
      title: "Configuración",
      icon: ICONS.settings,
      href: PATH_APP.tuCarta.settings,
      roles: [Roles.USER_NORMAL],
      isAdmin: true,
      condition: actions.includes(ROLES_ENUM.SETTINGS_MANAGER),
      items: [
        {
          title: "Impresoras",
          href: PATH_APP.tuCarta.printers,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.RESTAURANT_PRINTER_MANAGER),
        },
        {
          // Sustituye al antiguo nav item "Stripe" — ahora es el hub
          // unificado que incluye Stripe, Google Business y futuras
          // integraciones (bloque E onboarding v2).
          title: "Integraciones",
          href: PATH_APP.tuCarta.integrations,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.SETTINGS_STRIPE_MANAGER),
        },
        // F74 · Informes diarios ocultos temporalmente (decisión de producto).
        // La ruta sigue registrada; solo se ha quitado del nav.
        // {
        //   title: "Informes diarios",
        //   href: PATH_APP.tuCarta.reportsDaily,
        //   roles: [Roles.USER_NORMAL],
        //   isAdmin: true,
        //   condition: actions.includes(ROLES_ENUM.SETTINGS_ACCESS),
        // },
        {
          // F64 · Reseñas premium — requiere add-on "Reputación online"
          // o grant admin. Sin él, `isAddOnCta=true` pinta badge PRO
          // dorado y el click abre modal Stripe directo. Con add-on
          // activo, se pinta como item normal (sin "· PRO" en el título).
          title: "Reseñas",
          href: activeAddOnNames.includes("Reputación online")
            ? PATH_APP.tuCarta.reviews
            : "#addon:Reputación online",
          isAddOnCta: !activeAddOnNames.includes("Reputación online"),
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          // 2026-08-25 · Acción dedicada REVIEWS_MANAGE (antes usaba
          // SETTINGS_ACCESS que era el mismo permiso que Ajustes).
          condition: actions.includes("REVIEWS_MANAGE"),
        },
        {
          title: "Ajustes",
          href: PATH_APP.tuCarta.settings,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.SETTINGS_ACCESS),
        },
        {
          // 2026-08-24 · Sub-items de Ajustes ahora con condition
          // (antes aparecían siempre — cualquier rol de empleado los
          // veía en el nav aunque no tuviera permiso ni sentido).
          title: "Insights Monitor",
          href: PATH_APP.tuCarta.monitor,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.SETTINGS_MONITOR_ACCESS),
        },
        {
          title: "Auditoría",
          href: PATH_APP.user.audit,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.SETTINGS_AUDIT_ACCESS),
        },
        {
          title: "Actividad del TPV",
          href: PATH_APP.user.tpvActivity,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.AUDIT_TPV_VIEW),
        },
        {
          title: "Auditoría Hacienda",
          href: PATH_APP.user.aeatAudit,
          roles: [Roles.USER_NORMAL],
          isAdmin: true,
          condition: actions.includes(ROLES_ENUM.SETTINGS_AEAT_ACCESS),
        },
      ],
    },
  ];
};

const navConfigFranquicia = (user, conditionTpv, actions = []) => [
  {
    title: "Dashboard",
    icon: ICONS.dashboard,
    href: PATH_APP.franquicia.dashboard,
    roles: [Roles.USER_FRANQUICIA],
    isAdmin: true,
  },
  {
    title: "Franquicias",
    icon: ICONS.franquicias,
    href: PATH_APP.franquicia.franquicias,
    roles: [Roles.USER_FRANQUICIA],
    isAdmin: true,
  },
  {
    title: "Productos",
    icon: ICONS.cart,
    href: PATH_APP.franquicia.productos,
    roles: [Roles.USER_FRANQUICIA],
    isAdmin: true,
    condition: user?.productsAvailable,
  },
  {
    title: "Categorías",
    icon: ICONS.categorie,
    href: PATH_APP.franquicia.categories,
    roles: [Roles.USER_FRANQUICIA],
    isAdmin: true,
    condition: user?.categoriesAvailable,
  },
];

const navConfigFranquiciado = (user, conditionTpv, actions = []) => [
  {
    title: "Dashboard",
    icon: ICONS.dashboard,
    href: PATH_APP.franquiciado.restaurantCharts,
    roles: [Roles.USER_FRANQUICIADO],
    isAdmin: true,
  },
  {
    title: "TPV",
    href: PATH_APP.franquiciado.tpv,
    icon: ICONS.tpv,
    roles: [Roles.USER_FRANQUICIADO],
    isAdmin: false,
  },
  {
    title: "Caja",
    icon: ICONS.caja,
    href: PATH_APP.franquiciado.caja,
    roles: [Roles.USER_FRANQUICIADO],
    isAdmin: true,
    condition: user?.cajaAvailable,
  },
  {
    title: "Configuración",
    icon: ICONS.settings,
    href: PATH_APP.franquiciado.printers,
    roles: [Roles.USER_FRANQUICIADO],
    isAdmin: true,
    items: [
      {
        title: "Impresoras",
        href: PATH_APP.franquiciado.printers,
        roles: [Roles.USER_FRANQUICIADO],
      },
    ],
  },
];

const navConfigAdmin = (user, conditionTpv, actions = [], { socialAllowed = false } = {}) => [
  {
    title: "Licencias",
    icon: ICONS.user,
    href: PATH_APP.admin.licences,
    roles: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
    isAdmin: true,
  },
  {
    title: "Subscripciones",
    icon: ICONS.components,
    href: PATH_APP.admin.plans,
    roles: [Roles.GLOBAL_ADMIN],
    isAdmin: true,
  },
  {
    title: "Add-ons",
    icon: ICONS.components,
    href: PATH_APP.admin.addOns,
    roles: [Roles.GLOBAL_ADMIN],
    isAdmin: true,
  },
  {
    title: "Comerciales",
    icon: ICONS.user,
    href: PATH_APP.admin.distributors,
    roles: [Roles.GLOBAL_ADMIN],
    isAdmin: true,
  },
  {
    title: "Facturas",
    icon: ICONS.invoice,
    href: PATH_APP.admin.invoices,
    roles: [Roles.GLOBAL_ADMIN, Roles.USER_DISTRIBUTOR],
    isAdmin: true,
  },
  {
    title: "Notificaciones",
    icon: ICONS.email,
    href: PATH_APP.admin.notifications,
    roles: [Roles.GLOBAL_ADMIN],
    isAdmin: true,
  },
  {
    title: "Observability",
    icon: ICONS.logs,
    href: PATH_APP.admin.observability,
    roles: [Roles.GLOBAL_ADMIN],
    isAdmin: true,
  },
  {
    // 2026-10-26 · Sustituye a «Live debug» y «Versiones».
    title: "Centro de control",
    icon: ICONS.monitor,
    href: PATH_APP.admin.control,
    roles: [Roles.GLOBAL_ADMIN],
    isAdmin: true,
  },
  // 2026-10-24 · Publicaciones de TakeMyOrder en Instagram y Facebook. Solo
  // con `GET api/admin/social/access` → { allowed: true } (email en la lista
  // del API). Se inserta condicionalmente porque el dueño operando
  // (UserEmployeeId=0) se salta `condition` en el filtro del menú.
  ...(socialAllowed
    ? [
        {
          title: "Redes sociales",
          icon: ICONS.carousel,
          href: PATH_APP.admin.social,
          roles: [Roles.GLOBAL_ADMIN],
          isAdmin: true,
          condition: socialAllowed,
        },
      ]
    : []),
];

export default navConfig;
